/**
 * OpenAI 兼容 TTS 适配器（T10a）。
 *
 * 覆盖所有暴露 `POST /v1/audio/speech` 的服务：OpenAI 官方、OpenRouter 类网关、
 * 本地 OpenAI 兼容语音服务（如 GPT-SoVITS / CosyVoice 的兼容层）、自建网关等。
 * **不使用任何 SDK，只用原生 `fetch`。**
 *
 * 复用 T03 `src/core/providers/http.ts` 的全部基础设施，不另起一套：
 * - `normalizeBaseUrl` 规范化 baseUrl（自动补 `/v1`）；
 * - `openResponseWithRetry` 提供超时、失败重试与外部取消的组合；
 * - `TimeoutGuard` 在整段音频读取期间持续计时；
 * - `toProviderError` 把任意抛出物规范化为带错误码的错误。
 *
 * 关键行为：
 * - 成功：一次性返回 `{ audio: Blob, mime }`（二进制音频，**不解析文本**）；
 * - 取消：`AbortSignal` 触发时以 `AbortError` 结束，**绝不返回半截音频**；
 * - HTTP / 超时 / 网络 / 解析错误：抛出 `TtsError`，携带规范化错误码；
 * - `listVoices()` 调 `/v1/audio/voices`；服务端不支持该端点（404 / 405 / 501）时
 *   回退到 OpenAI 官方内置音色清单，保证设置页始终有可选项。
 */

import {
  clampRetries,
  clampRetryDelayMs,
  clampTimeoutMs,
  jsonHeaders,
  normalizeBaseUrl,
  openResponseWithRetry,
  ProviderHttpError,
  readJsonBody,
  resolveDeps,
  toProviderError,
  type OpenResponseResult,
} from '../providers/http';
import type { FetchLike } from '../providers/types';
import {
  TtsError,
  type ResolvedTtsConfig,
  type TtsAdapter,
  type TtsDeps,
  type TtsErrorCode,
  type TtsFormat,
  type TtsRequest,
  type TtsResult,
} from './types';

export const OPENAI_TTS_KIND = 'openai-compatible' as const;

/** OpenAI 官方内置音色（`/v1/audio/speech` 的 `voice` 取值）。 */
export const DEFAULT_OPENAI_VOICES: readonly string[] = [
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'fable',
  'nova',
  'onyx',
  'sage',
  'shimmer',
] as const;

/** `response_format` -> MIME 类型；仅在响应未带可用 `Content-Type` 时兜底。 */
export const FORMAT_MIME: Record<TtsFormat, string> = {
  mp3: 'audio/mpeg',
  opus: 'audio/ogg',
  aac: 'audio/aac',
  flac: 'audio/flac',
  wav: 'audio/wav',
  pcm: 'audio/L16',
};

export const DEFAULT_TTS_FORMAT: TtsFormat = 'mp3';

/** `/v1/audio/speech` 请求体（OpenAI 兼容字段）。 */
export interface OpenAISpeechRequestBody {
  model: string;
  input: string;
  voice: string;
  response_format: TtsFormat;
  speed?: number;
  [key: string]: unknown;
}

/**
 * 在规范化 baseUrl 之后追加路径段。
 *
 * 与 T03 `http.ts` 的 `appendPath` 同一实现思路，但本任务不允许改动既有文件，
 * 因此这里用导出的 `normalizeBaseUrl` 复刻。必须走 `URL` 拼接而不是字符串相加：
 * baseUrl 可能带 query / hash。
 */
function appendPath(baseUrl: string, segment: string): string {
  const url = new URL(normalizeBaseUrl(baseUrl));
  url.pathname = `${url.pathname.replace(/\/+$/, '')}${segment}`;
  return url.toString();
}

/** `/v1/audio/speech` 完整地址。 */
export function speechUrl(baseUrl: string): string {
  return appendPath(baseUrl, '/audio/speech');
}

/** `/v1/audio/voices` 完整地址。 */
export function voicesUrl(baseUrl: string): string {
  return appendPath(baseUrl, '/audio/voices');
}

/** 构造一个真正的 `AbortError`（浏览器 / Node 18+ 用 DOMException）。 */
export function createAbortError(reason?: unknown): Error {
  const message = typeof reason === 'string' && reason ? reason : '请求已被取消';
  if (typeof DOMException === 'function') {
    return new DOMException(message, 'AbortError');
  }
  const error = new Error(message);
  error.name = 'AbortError';
  return error;
}

/** 判断是否为 `AbortError`。 */
export function isAbortError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/** 把任意抛出物规范化为 `TtsError`（保留 T03 的错误码与 HTTP 状态）。 */
export function toTtsError(error: unknown, fallbackCode: TtsErrorCode = 'network'): TtsError {
  if (error instanceof TtsError) return error;
  const normalized = toProviderError(error, fallbackCode);
  return new TtsError(normalized.message, normalized.code, normalized.status);
}

/**
 * 组装 `/v1/audio/speech` 请求体。
 *
 * 字段映射：`model` / `input` / `voice` / `response_format`，`speed` 仅在提供时下发。
 * `voice` 与 `model` 缺失时抛 `config` 错误，而不是发出必然失败的请求。
 */
export function buildSpeechBody(
  req: TtsRequest,
  config: ResolvedTtsConfig,
): OpenAISpeechRequestBody {
  const text = req.text ?? '';
  if (!text.trim()) {
    throw new TtsError('要朗读的文本为空', 'config');
  }

  // 请求级取值优先，回退到配置默认值；空串同样视为「未提供」。
  const requestedModel = req.model?.trim() ?? '';
  const configuredModel = config.defaultModel?.trim() ?? '';
  const model = requestedModel.length > 0 ? requestedModel : configuredModel;
  if (!model) {
    throw new TtsError('未指定 TTS 模型（请求未提供且配置无 defaultModel）', 'config');
  }

  const requestedVoice = req.voice?.trim() ?? '';
  const configuredVoice = config.defaultVoice?.trim() ?? '';
  const voice = requestedVoice.length > 0 ? requestedVoice : configuredVoice;
  if (!voice) {
    throw new TtsError('未指定音色（请求未提供且配置无 defaultVoice）', 'config');
  }

  const body: OpenAISpeechRequestBody = {
    model,
    input: text,
    voice,
    response_format: req.format ?? DEFAULT_TTS_FORMAT,
  };
  if (req.speed !== undefined) body.speed = req.speed;
  return body;
}

/** 从响应头推导音频 MIME；缺失或不可用时按请求格式兜底。 */
export function resolveMime(contentType: string | null, format: TtsFormat): string {
  const value = contentType?.split(';')[0]?.trim().toLowerCase() ?? '';
  if (value && value !== 'application/octet-stream' && value !== 'application/json') {
    return value;
  }
  return FORMAT_MIME[format];
}

/** 从 `/v1/audio/voices` 的多种返回形态里抽取音色 id。 */
export function collectVoiceIds(json: unknown): string[] {
  if (!json || typeof json !== 'object') return [];
  const record = json as { data?: unknown; voices?: unknown };
  const source = Array.isArray(record.data) ? record.data : record.voices;
  if (!Array.isArray(source)) return [];

  const ids: string[] = [];
  for (const item of source) {
    if (typeof item === 'string' && item.length > 0) {
      ids.push(item);
      continue;
    }
    if (item && typeof item === 'object' && 'id' in item) {
      const id = (item as { id?: unknown }).id;
      if (typeof id === 'string' && id.length > 0) ids.push(id);
    }
  }
  return ids;
}

export class OpenAICompatibleTtsAdapter implements TtsAdapter {
  readonly id: string;
  readonly kind = OPENAI_TTS_KIND;
  private readonly config: ResolvedTtsConfig;
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;
  private readonly retries: number;
  private readonly retryDelayMs: number;
  private readonly extraHeaders: Record<string, string>;

  constructor(config: ResolvedTtsConfig, deps?: TtsDeps) {
    // TtsDeps 与 T03 ProviderDeps 结构一致，可直接复用同一套边界钳制。
    const resolved = resolveDeps(deps);
    this.config = config;
    this.id = config.id;
    this.fetchImpl = resolved.fetch;
    // 配置值优先，但一律过边界校验（负数 / NaN 不得绕过钳制）。
    this.timeoutMs = clampTimeoutMs(config.timeoutMs, resolved.timeoutMs);
    this.retries = clampRetries(config.retries, resolved.retries);
    this.retryDelayMs = clampRetryDelayMs(config.retryDelayMs, resolved.retryDelayMs);
    // 自定义头：TtsConfig.headers 覆盖 TtsDeps.extraHeaders。
    this.extraHeaders = { ...resolved.extraHeaders, ...(config.headers ?? {}) };
  }

  private headers(): Record<string, string> {
    // POST body 是 JSON，但期望的响应是二进制音频，因此 Accept 覆盖为 audio/*。
    return { ...jsonHeaders(this.config.apiKey, this.extraHeaders), Accept: 'audio/*' };
  }

  private deps() {
    return {
      fetch: this.fetchImpl,
      timeoutMs: this.timeoutMs,
      retries: this.retries,
      retryDelayMs: this.retryDelayMs,
      extraHeaders: this.extraHeaders,
    };
  }

  async listVoices(): Promise<string[]> {
    let opened: OpenResponseResult;
    try {
      opened = await openResponseWithRetry({
        url: voicesUrl(this.config.baseUrl),
        init: { method: 'GET', headers: this.headers() },
        deps: this.deps(),
      });
    } catch (error) {
      const normalized = toTtsError(error);
      // 该端点并非 OpenAI 官方标准；服务端不支持时回退到内置音色清单，
      // 而不是让整个设置页报错。
      if (
        normalized.code === 'http' &&
        (normalized.status === 404 || normalized.status === 405 || normalized.status === 501)
      ) {
        return [...DEFAULT_OPENAI_VOICES];
      }
      throw normalized;
    }

    const { response, guard } = opened;
    try {
      const json = await readJsonBody<unknown>(response);
      const ids = collectVoiceIds(json);
      return ids.length > 0 ? ids : [...DEFAULT_OPENAI_VOICES];
    } finally {
      guard.dispose();
    }
  }

  async speak(req: TtsRequest): Promise<TtsResult> {
    // 契约：取消 = 以 AbortError 结束，绝不返回半截音频。
    if (req.signal?.aborted) throw createAbortError(req.signal.reason);

    const body = buildSpeechBody(req, this.config);

    let opened: OpenResponseResult;
    try {
      opened = await openResponseWithRetry({
        url: speechUrl(this.config.baseUrl),
        init: {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify(body),
        },
        deps: this.deps(),
        externalSignal: req.signal,
      });
    } catch (error) {
      if (req.signal?.aborted) throw createAbortError(req.signal.reason);
      throw toTtsError(error);
    }

    const { response, guard } = opened;
    // 出错 / 取消时主动中止底层请求，释放连接，避免 body 悬挂。
    let earlyExit = false;

    try {
      const audio = await response.blob();
      // blob() 期间被取消：丢弃已读到的字节，按取消契约结束。
      if (req.signal?.aborted) {
        earlyExit = true;
        throw createAbortError(req.signal.reason);
      }
      if (audio.size === 0) {
        earlyExit = true;
        throw new TtsError('响应音频为空', 'parse');
      }
      return {
        audio,
        mime: resolveMime(response.headers.get('content-type'), body.response_format),
      };
    } catch (error) {
      earlyExit = true;
      if (req.signal?.aborted) throw createAbortError(req.signal.reason);
      if (guard.timedOut()) {
        throw new TtsError(`请求超时（${this.timeoutMs}ms）`, 'timeout');
      }
      throw toTtsError(error, 'parse');
    } finally {
      if (earlyExit) guard.abort();
      guard.dispose();
    }
  }
}

export function createOpenAICompatibleTtsAdapter(
  config: ResolvedTtsConfig,
  deps?: TtsDeps,
): TtsAdapter {
  return new OpenAICompatibleTtsAdapter(config, deps);
}

export { ProviderHttpError };
